// Sidebar.jsx
import React, { useState, useContext, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import LanguageSelector from "./LanguageSelector";
import SearchChatModal from "./SearchChatModal";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import { getAuth, signOut } from "firebase/auth";
import ToggleSwitch from "./ToggleSwitch";
import {
  FiHome,
  FiEdit,
  FiLogOut,
  FiMessageSquare,
  FiUser,
  FiSearch,
  FiMoreVertical,
  FiChevronsLeft,
  FiChevronsRight,
  FiFileText,
  FiTrendingUp,
  FiDollarSign,
  FiLock,
  FiShield,
  FiEye,
  FiBarChart2,
  FiChevronDown,
  FiChevronRight,
} from "react-icons/fi";
import { FaUserCircle } from "react-icons/fa";
import { MdDelete, MdOutlineDriveFileRenameOutline, MdArchive, MdClose } from "react-icons/md";
import { FiAlertCircle } from "react-icons/fi";

const HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

// ✅ MUST match ChatInterface
const ACTIVE_CHAT_STORAGE_KEY = "active_chat";
const SUPPRESS_RESTORE_KEY = "suppress_chat_restore_until";

const Sidebar = ({
  isCollapsed,
  setIsCollapsed,
  isSidebarOpen,
  setIsSidebarOpen,
  isMobile,
  isTablet,
  chatList = [],
  visionList = [],

  // ✅ Sidebar pagination is owned by Layout/backend; Sidebar only renders controls.
  chatHasMore = false,
  visionHasMore = false,
  chatLoadMorePending = false,
  visionLoadMorePending = false,
  onLoadMoreChats,
  onLoadMoreVision,

  setShowSearchModal: setShowSearchModalFromLayout, // Layout passes this (optional)

  // ✅ Regular chat selection (existing)
  activeChat,
  setActiveChat,

  // ✅ Vision selection (new, separate from activeChat)
  activeVision,
  setActiveVision,

  handleNewChat, // Layout passes this (optional)
}) => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, setUser } = useContext(AuthContext);

  const [menuOpenId, setMenuOpenId] = useState(null);
  const [showSearchModalLocal, setShowSearchModalLocal] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [caseAnalyzerOpen, setCaseAnalyzerOpen] = useState(false);
  const [contractInsightOpen, setContractInsightOpen] = useState(false);
  const [docWriterOpen, setDocWriterOpen] = useState(false);

  // ✅ NEW: instant highlight even before parent/dashboard state fully propagates
  const [pendingActive, setPendingActive] = useState({ type: null, sid: null });

  const auth = getAuth();

  // ✅ Unified narrow logic (<=1024): mobile + tablet behave the same (drawer / hamburger)
  const isNarrow = Boolean(isMobile || isTablet);

  // ✅ Pricing/My Plan label + icon (same route /billing)
  const plansLabel = currentUser ? "My Plan" : "Pricing";
  const plansIcon = currentUser ? "💳" : <FiDollarSign />;

  const openSearchModal = () => {
    setShowSearchModalLocal(true);
    if (typeof setShowSearchModalFromLayout === "function") {
      setShowSearchModalFromLayout(true);
    }
  };

  const closeSearchModal = () => {
    setShowSearchModalLocal(false);
    if (typeof setShowSearchModalFromLayout === "function") {
      setShowSearchModalFromLayout(false);
    }
  };

  useEffect(() => {
    if (isMobile || isTablet) setIsSidebarOpen(false);
  }, [location.pathname]); // keep your original behavior

  // ✅ ENFORCE: On narrow devices, sidebar is NEVER collapsed (drawer width must be full)
  useEffect(() => {
    if (isNarrow && isCollapsed) {
      setIsCollapsed(false);
    }
  }, [isNarrow, isCollapsed, setIsCollapsed]);

  // ✅ FIX: Clear thread highlight when navigating away from thread context
  // - Threads live on /dashboard (chat) and /vision/session/* (Vision Assist session)
  // - Any other page (About/Contact/Home/etc.) should NOT keep a thread highlighted
  useEffect(() => {
    try {
      const p = String(location?.pathname || "");
      const isThreadContext = p.startsWith("/dashboard") || p.startsWith("/vision/session/");
      if (!isThreadContext) {
        setPendingActive({ type: null, sid: null });
        setActiveChat?.(null);
        setActiveVision?.(null);
      }
    } catch {}
  }, [location.pathname]);

  // ✅ Close dropdown when clicking outside
  useEffect(() => {
    const onDoc = (e) => {
      try {
        if (!e?.target?.closest?.(".sidebar-menu-wrapper")) {
          setMenuOpenId(null);
        }
      } catch {
        setMenuOpenId(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // ✅ Keep pendingActive aligned to true active selection (so it self-corrects)
  useEffect(() => {
    if (activeChat?.session_id) {
      setPendingActive({ type: "chat", sid: activeChat.session_id });
      return;
    }
    if (activeVision?.session_id) {
      setPendingActive({ type: "vision", sid: activeVision.session_id });
      return;
    }
    setPendingActive({ type: null, sid: null });
  }, [activeChat?.session_id, activeVision?.session_id]);

  const fireRefreshSidebar = () => {
    try {
      window.dispatchEvent(new Event("triggerRefreshSidebar"));
    } catch {}
  };

  // ✅ NEW CHAT: always navigate to dashboard + clear selections (NO backend session creation here)
  const handleNewChatClick = () => {
    try {
      setMenuOpenId(null);

      // ✅ Clear pending highlight too
      setPendingActive({ type: null, sid: null });

      // ✅ Always clear both selections for a true "new chat" start
      setActiveVision?.(null);
      setActiveChat?.(null);

      // ✅ HARD clear persisted chat + draft
      try {
        localStorage.removeItem(ACTIVE_CHAT_STORAGE_KEY);
        localStorage.removeItem("chat_input");

        // ✅ Prevent ChatInterface restore from snapping back to the previous thread
        localStorage.setItem(SUPPRESS_RESTORE_KEY, String(Date.now() + 15000)); // 15s
      } catch {}

      // ✅ Tell ChatInterface to clear instantly (even if parent state lags)
      try {
        window.dispatchEvent(new Event("asklawly_new_chat"));
      } catch {}

      // ✅ Let Layout refresh lists / reset any state it wants
      if (typeof handleNewChat === "function") {
        handleNewChat();
      } else {
        fireRefreshSidebar();
      }

      if (isNarrow) setIsSidebarOpen(false);

      // ✅ Always land on dashboard (logged in or not)
      navigate("/dashboard");
    } catch (err) {
      console.error("[Sidebar] handleNewChatClick failed ❌", err);
      if (isNarrow) setIsSidebarOpen(false);
      navigate("/dashboard");
    }
  };

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      await signOut(auth);
    } catch (err) {
      console.error("[Sidebar] Logout failed ❌", err);
    }
    setUser(null);
    localStorage.removeItem("jwt_token");
    navigate("/login");
    if (isNarrow) setIsSidebarOpen(false);
  };

  const handleRenameChat = async (chat) => {
    const current = chat?.session_name || chat?.title || "";
    const newName = prompt("Enter a new name for the chat:", current);
    if (!newName || !newName.trim()) return;

    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();

      const res = await axios.post(
        `${HOST}/chat/rename`,
        { session_id: chat.session_id, new_name: newName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.success) {
        setMenuOpenId(null);
        fireRefreshSidebar();
      } else {
        alert("Failed to rename chat.");
      }
    } catch (err) {
      alert("Something went wrong while renaming the chat.");
    }
  };

  const handleArchiveChat = async (chat) => {
    const sessionId = chat?.session_id || "";
    if (!sessionId) return alert("Session ID is missing. Cannot archive.");

    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();

      const res = await axios.post(
        `${HOST}/chat/archive`,
        { session_id: sessionId, archive: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.success) {
        setMenuOpenId(null);
        fireRefreshSidebar();
      } else {
        alert("Failed to archive chat.");
      }
    } catch (err) {
      alert("Something went wrong while archiving.");
    }
  };

  const handleDeleteChat = async (chat) => {
    if (!window.confirm("Are you sure you want to delete this chat?")) return;

    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();

      const res = await axios.post(
        `${HOST}/chat/delete`,
        { session_id: chat.session_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.success) {
        setMenuOpenId(null);
        fireRefreshSidebar();
      } else {
        alert("Failed to delete the chat.");
      }
    } catch (err) {
      alert("Something went wrong while deleting the chat.");
    }
  };

  // ✅ NEW: Vision menu actions (Rename / Archive / Delete) using vision_assist_list_handler.py
  const handleRenameVision = async (vs) => {
    const sid = vs?.session_id || "";
    if (!sid) return;

    const current = vs?.session_name || "";
    const newName = prompt("Enter a new name for the vision session:", current);
    if (!newName || !newName.trim()) return;

    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();

      const res = await axios.post(
        `${HOST}/vision/session/${encodeURIComponent(sid)}/rename`,
        { session_name: newName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.success) {
        setMenuOpenId(null);
        fireRefreshSidebar();
      } else {
        alert("Failed to rename vision session.");
      }
    } catch (err) {
      alert("Something went wrong while renaming the vision session.");
    }
  };

  const handleArchiveVision = async (vs) => {
    const sid = vs?.session_id || "";
    if (!sid) return alert("Session ID is missing. Cannot archive.");

    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();

      const res = await axios.post(
        `${HOST}/vision/session/${encodeURIComponent(sid)}/archive`,
        { archived: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.success) {
        setMenuOpenId(null);
        fireRefreshSidebar();

        if (activeVision?.session_id && activeVision.session_id === sid) {
          setActiveVision?.(null);
          setPendingActive({ type: null, sid: null });
        }
      } else {
        alert("Failed to archive vision session.");
      }
    } catch (err) {
      alert("Something went wrong while archiving the vision session.");
    }
  };

  const handleDeleteVision = async (vs) => {
    const sid = vs?.session_id || "";
    if (!sid) return;

    if (!window.confirm("Are you sure you want to delete this vision session?")) return;

    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();

      const res = await axios.post(
        `${HOST}/vision/session/${encodeURIComponent(sid)}/delete`,
        { deleted: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.success) {
        setMenuOpenId(null);
        fireRefreshSidebar();

        if (activeVision?.session_id && activeVision.session_id === sid) {
          setActiveVision?.(null);
          setPendingActive({ type: null, sid: null });
          navigate("/dashboard");
        }
      } else {
        alert("Failed to delete vision session.");
      }
    } catch (err) {
      alert("Something went wrong while deleting the vision session.");
    }
  };

  // ✅ Open Vision Assist session by session_id (matches backend list item: session_id)
  const handleOpenVisionSession = (session) => {
    setMenuOpenId(null);
    const sid = session?.session_id;
    if (!sid) return;

    // ✅ Immediate highlight
    setPendingActive({ type: "vision", sid });

    // ✅ Selecting a vision session clears active chat
    setActiveChat?.(null);
    setActiveVision?.({ session_id: sid });

    // ✅ Persistent Vision Assist history reopens the actual session page.
    // The session page owns history hydration, read-only/zero-minute behavior,
    // and any continuation billing/access checks.
    navigate(`/vision/session/${sid}`);

    if (isNarrow) setIsSidebarOpen(false);
  };

  // ✅ Filtering: default hides archived; Show All reveals archived + unarchived
  const filteredChats = (chatList || []).filter((c) => (showAll ? true : !c?.archived));
  const filteredVision = (visionList || []).filter((v) => (showAll ? true : !v?.archived));

  // ✅ Existence (archived or not) excluding deleted
  const hasAnyChatNonDeleted = (chatList || []).some((c) => !c?.deleted);
  const hasAnyVisionNonDeleted = (visionList || []).some((v) => !v?.deleted);
  const hasAnyThreadNonDeleted = Boolean(hasAnyChatNonDeleted || hasAnyVisionNonDeleted);

  const shouldShowChatsSection = Boolean(currentUser && hasAnyThreadNonDeleted);
  const shouldShowShowAllToggle = Boolean(currentUser && !isCollapsed && hasAnyThreadNonDeleted);

  // ✅ Chats header should remain highlighted when any thread is active
  const isChatsSectionActive = Boolean(
    (activeChat?.session_id && !activeVision?.session_id) ||
      (activeVision?.session_id && !activeChat?.session_id) ||
      (pendingActive?.sid && (pendingActive.type === "chat" || pendingActive.type === "vision"))
  );

  // ✅ For Vision list display
  const getVisionTitle = (v, idx) => v?.session_name || `Vision ${idx + 1}`;
  const getVisionPreview = (v) => (v?.preview || "").trim();

  // ✅ Chat title display: show "Chat MM-DD-YYYY" when backend sends the legacy "Chat on ..." default
  const pad2 = (n) => String(n).padStart(2, "0");

  const coerceToDate = (ts) => {
    try {
      if (!ts) return null;
      if (ts instanceof Date) return ts;
      if (typeof ts.toDate === "function") return ts.toDate(); // Firestore Timestamp
      if (typeof ts.seconds === "number") return new Date(ts.seconds * 1000);
      if (typeof ts._seconds === "number") return new Date(ts._seconds * 1000);
      return null;
    } catch {
      return null;
    }
  };

  const formatChatMMDDYYYY = (d) => {
    if (!d || isNaN(d.getTime?.())) return null;
    return `Chat ${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}-${d.getFullYear()}`;
  };

  const tryParseLegacyChatOn = (name) => {
    try {
      if (!name || typeof name !== "string") return null;
      if (!name.startsWith("Chat on ")) return null;
      const raw = name.replace(/^Chat on\s+/i, "").trim();
      const dt = new Date(raw);
      if (isNaN(dt.getTime())) return null;
      return dt;
    } catch {
      return null;
    }
  };

  const getChatTitle = (chat, idx) => {
    const raw = (chat?.session_name || chat?.title || "").toString();
    if (raw.startsWith("Chat on ")) {
      const d1 = coerceToDate(chat?.created_at);
      const fmt1 = formatChatMMDDYYYY(d1);
      if (fmt1) return fmt1;

      const d2 = tryParseLegacyChatOn(raw);
      const fmt2 = formatChatMMDDYYYY(d2);
      if (fmt2) return fmt2;
    }
    return raw || `Untitled Chat ${idx + 1}`;
  };

  return (
    <>
      {isNarrow && isSidebarOpen && <div style={styles.overlay} onClick={() => setIsSidebarOpen(false)} />}

      <div
        style={{
          ...styles.sidebar,
          width: isNarrow ? "260px" : isCollapsed ? "60px" : "260px",
          transform: isNarrow && !isSidebarOpen ? "translateX(-100%)" : "translateX(0)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* 🔵 Top Section */}
        <div
          style={{
            flex: "0 0 6%",
            minHeight: "20px",
            borderBottom: "1px solid #ccc",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 15px",
          }}
        >
          <Link
            to="/home"
            onClick={() => {
              if (isNarrow) setIsSidebarOpen(false);
            }}
            style={{
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
            }}
            aria-label="Go to Home"
          >
            <div
              style={{
                padding: isCollapsed ? "10px 0 10px 20px" : "10px 20px 10px 0px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/assets/logo-full.png`}
                alt="Full Logo"
                className={`sidebar-logo ${isCollapsed ? "collapsed" : ""}`}
                style={{ width: "160px", height: "auto", objectFit: "contain" }}
              />
            </div>
          </Link>

          {!isNarrow ? (
            <button onClick={() => setIsCollapsed((prev) => !prev)} style={styles.collapseBtn} type="button">
              {isCollapsed ? <FiChevronsRight /> : <FiChevronsLeft />}
            </button>
          ) : (
            <button onClick={() => setIsSidebarOpen(false)} style={styles.collapseBtn} type="button">
              <MdClose style={{ fontSize: "28px" }} />
            </button>
          )}
        </div>

        {/* 🟢 Scrollable Middle Section */}
        <div className="sidebar-middle-scroll">
          <div style={styles.nav}>
            <SidebarLink
              to="/home"
              icon={<FiHome />}
              label="Home"
              isCollapsed={isCollapsed}
              onClick={() => {
                if (isNarrow) setIsSidebarOpen(false);
              }}
            />

            <SidebarButton icon={<FiEdit />} label="New Chat" isCollapsed={isCollapsed} onClick={handleNewChatClick} />

            <SidebarButton
              icon={<FiSearch />}
              label="Search Chat"
              isCollapsed={isCollapsed}
              onClick={() => {
                openSearchModal();
                if (isNarrow) setIsSidebarOpen(false);
              }}
            />

            <SidebarButton
              icon={<FiBarChart2 />}
              label="Case Analyzer"
              isCollapsed={isCollapsed}
              onClick={() => {
                if (isCollapsed) {
                  navigate("/case-analyzer");
                  if (isNarrow) setIsSidebarOpen(false);
                  return;
                }
                setCaseAnalyzerOpen((prev) => !prev);
              }}
              isActive={String(location.pathname || "").startsWith("/case-analyzer")}
              trailingIcon={!isCollapsed ? (caseAnalyzerOpen ? <FiChevronDown /> : <FiChevronRight />) : null}
            />

            {!isCollapsed && caseAnalyzerOpen && (
              <div style={styles.subNavGroup}>
                <SidebarSubLink
                  to="/case-analyzer"
                  label="New Analysis"
                  isActive={location.pathname === "/case-analyzer"}
                  onClick={() => {
                    if (isNarrow) setIsSidebarOpen(false);
                  }}
                />
                <SidebarSubLink
                  to="/case-analyzer/history"
                  label="Analysis History"
                  isActive={location.pathname === "/case-analyzer/history"}
                  onClick={() => {
                    if (isNarrow) setIsSidebarOpen(false);
                  }}
                />
              </div>
            )}

            <SidebarLink
              to="/cost-pilot"
              icon={<FiTrendingUp />}
              label="Case Estimator"
              isCollapsed={isCollapsed}
              onClick={() => setIsSidebarOpen(false)}
            />

            <SidebarLink
              to="/case-value"
              icon={<FiDollarSign />}
              label="Case Value"
              isCollapsed={isCollapsed}
              onClick={() => setIsSidebarOpen(false)}
            />

            <SidebarButton
              icon={<FiShield />}
              label="Contract Insight"
              isCollapsed={isCollapsed}
              onClick={() => {
                if (isCollapsed) {
                  navigate("/contract-insight");
                  if (isNarrow) setIsSidebarOpen(false);
                  return;
                }
                setContractInsightOpen((prev) => !prev);
              }}
              isActive={String(location.pathname || "").startsWith("/contract-insight")}
              trailingIcon={!isCollapsed ? (contractInsightOpen ? <FiChevronDown /> : <FiChevronRight />) : null}
            />

            {!isCollapsed && contractInsightOpen && (
              <div style={styles.subNavGroup}>
                <SidebarSubLink
                  to="/contract-insight"
                  label="New Analysis"
                  isActive={location.pathname === "/contract-insight"}
                  onClick={() => {
                    if (isNarrow) setIsSidebarOpen(false);
                  }}
                />
                <SidebarSubLink
                  to="/contract-insight/history"
                  label="Analysis History"
                  isActive={
                    location.pathname === "/contract-insight/history" ||
                    location.pathname === "/contract-insight/dashboard"
                  }
                  onClick={() => {
                    if (isNarrow) setIsSidebarOpen(false);
                  }}
                />
              </div>
            )}

            <SidebarButton
              icon={<FiFileText />}
              label="Doc Writer"
              isCollapsed={isCollapsed}
              onClick={() => {
                if (isCollapsed) {
                  navigate("/docwriter/start");
                  if (isNarrow) setIsSidebarOpen(false);
                  return;
                }
                setDocWriterOpen((prev) => !prev);
              }}
              isActive={String(location.pathname || "").startsWith("/docwriter")}
              trailingIcon={!isCollapsed ? (docWriterOpen ? <FiChevronDown /> : <FiChevronRight />) : null}
            />

            {!isCollapsed && docWriterOpen && (
              <div style={styles.subNavGroup}>
                <SidebarSubLink
                  to="/docwriter/start"
                  label="New Doc"
                  isActive={location.pathname === "/docwriter/start"}
                  onClick={() => {
                    if (isNarrow) setIsSidebarOpen(false);
                  }}
                />
                <SidebarSubLink
                  to="/docwriter/history"
                  label="Doc History"
                  isActive={location.pathname === "/docwriter/history"}
                  onClick={() => {
                    if (isNarrow) setIsSidebarOpen(false);
                  }}
                />
              </div>
            )}

            <SidebarLink
              to="/vision-assist"
              icon={<FiEye />}
              label="Vision Assist"
              isCollapsed={isCollapsed}
              onClick={() => setIsSidebarOpen(false)}
            />

            <SidebarLink
              to="/file-vault"
              icon={<FiLock />}
              label="File Vault"
              isCollapsed={isCollapsed}
              onClick={() => setIsSidebarOpen(false)}
            />

            <SidebarLink
              to="/smart-alert"
              icon={<FiAlertCircle />}
              label={translate("Smart Alert")}
              isCollapsed={isCollapsed}
              onClick={() => setIsSidebarOpen(false)}
            />

            {currentUser && <SidebarLink to="/profile" icon={<FaUserCircle />} label="My Profile" isCollapsed={isCollapsed} onClick={() => setIsSidebarOpen(false)} />}

            {/* ✅ SAME PAGE: logged out shows "Pricing", logged in shows "My Plan" */}
            <SidebarLink
              to="/billing"
              icon={plansIcon}
              label={plansLabel}
              isCollapsed={isCollapsed}
              onClick={() => setIsSidebarOpen(false)}
            />

            <SidebarLink to="/about" icon="ℹ️" label="About Us" isCollapsed={isCollapsed} onClick={() => setIsSidebarOpen(false)} />
            <SidebarLink to="/contact" icon="📬" label="Contact Us" isCollapsed={isCollapsed} onClick={() => setIsSidebarOpen(false)} />

            {/* ✅ Chats header appears only if any chat/vision thread exists */}
            {shouldShowChatsSection && (
              <>
                <SidebarButton icon={<FiMessageSquare />} label="Chats" isCollapsed={isCollapsed} onClick={() => {}} isActive={isChatsSectionActive} />

                {shouldShowShowAllToggle && (
                  <div style={{ display: "flex", alignItems: "center", padding: "10px 20px" }}>
                    <div style={{ minWidth: "24px" }} />
                    <div style={{ marginLeft: "10px" }}>
                      <ToggleSwitch label="Show All" value={showAll} onChange={setShowAll} />
                    </div>
                  </div>
                )}

                <div style={styles.historyContainer}>
                  {/* CHAT LIST */}
                  {currentUser &&
                    !isCollapsed &&
                    filteredChats.map((chat, index) => {
                      const sid = chat?.session_id || "";

                      const isActive = Boolean(
                        (activeChat?.session_id && sid && activeChat.session_id === sid) ||
                          (pendingActive.type === "chat" && pendingActive.sid === sid)
                      );

                      const displayTitle = getChatTitle(chat, index);

                      return (
                        <div key={sid || index} style={{ ...styles.chatItem, ...(isActive ? styles.activeItem : null) }}>
                          <div style={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                            <div style={styles.threadIcon}>
                              <FiEdit />
                            </div>

                            <div
                              onClick={() => {
                                setMenuOpenId(null);

                                // ✅ immediate highlight
                                setPendingActive({ type: "chat", sid });

                                // ✅ selecting chat clears vision selection
                                setActiveVision?.(null);

                                // ✅ ensure new object reference (prevents React bailout edge cases)
                                setActiveChat?.({ ...chat });

                                navigate("/dashboard");
                                if (isNarrow) setIsSidebarOpen(false);
                              }}
                              style={styles.chatTitle}
                              title={displayTitle}
                            >
                              {displayTitle}
                            </div>
                          </div>

                          <div style={styles.menuWrapper} className="sidebar-menu-wrapper">
                            <FiMoreVertical
                              style={styles.moreIcon}
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuOpenId(menuOpenId === sid ? null : sid);
                              }}
                            />
                            {menuOpenId === sid && (
                              <div
                                style={styles.dropdown}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                <div style={styles.dropdownItem} onClick={() => handleRenameChat(chat)}>
                                  <MdOutlineDriveFileRenameOutline /> Rename
                                </div>
                                <div style={styles.dropdownItem} onClick={() => handleArchiveChat(chat)}>
                                  <MdArchive /> Archive
                                </div>
                                <div style={styles.dropdownItem} onClick={() => handleDeleteChat(chat)}>
                                  <MdDelete /> Delete
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                  {currentUser &&
                    !isCollapsed &&
                    chatHasMore &&
                    typeof onLoadMoreChats === "function" && (
                      <div style={styles.loadMoreWrap}>
                        <button
                          type="button"
                          style={{
                            ...styles.loadMoreBtn,
                            ...(chatLoadMorePending ? styles.loadMoreBtnDisabled : null),
                          }}
                          onClick={onLoadMoreChats}
                          disabled={chatLoadMorePending}
                          aria-label="Load more chat sessions"
                        >
                          {chatLoadMorePending ? "Loading…" : "Load more"}
                        </button>
                      </div>
                    )}

                  {/* VISION LIST */}
                  {currentUser &&
                    !isCollapsed &&
                    filteredVision.map((vs, index) => {
                      const sid = vs?.session_id || "";
                      const title = getVisionTitle(vs, index);
                      const preview = getVisionPreview(vs);

                      const isVisionActive = Boolean(
                        (activeVision?.session_id && sid && activeVision.session_id === sid) ||
                          (pendingActive.type === "vision" && pendingActive.sid === sid)
                      );

                      const menuId = `vision:${sid || index}`;

                      return (
                        <div key={sid || `vision-${index}`} style={{ ...styles.visionItem, ...(isVisionActive ? styles.activeItem : null) }}>
                          <div style={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                            <div style={styles.threadIcon}>
                              <FiEye />
                            </div>

                            <div onClick={() => handleOpenVisionSession(vs)} style={styles.visionTitleWrap} title={title}>
                              <div style={styles.visionTitleLine}>
                                <span style={styles.visionTitleText}>{title}</span>
                              </div>

                              {!!preview && <div style={styles.visionPreview}>{preview}</div>}
                            </div>
                          </div>

                          <div style={styles.menuWrapper} className="sidebar-menu-wrapper">
                            <FiMoreVertical
                              style={styles.moreIcon}
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuOpenId(menuOpenId === menuId ? null : menuId);
                              }}
                            />
                            {menuOpenId === menuId && (
                              <div
                                style={styles.dropdown}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                <div style={styles.dropdownItem} onClick={() => handleRenameVision(vs)}>
                                  <MdOutlineDriveFileRenameOutline /> Rename
                                </div>
                                <div style={styles.dropdownItem} onClick={() => handleArchiveVision(vs)}>
                                  <MdArchive /> Archive
                                </div>
                                <div style={styles.dropdownItem} onClick={() => handleDeleteVision(vs)}>
                                  <MdDelete /> Delete
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                  {currentUser &&
                    !isCollapsed &&
                    visionHasMore &&
                    typeof onLoadMoreVision === "function" && (
                      <div style={styles.loadMoreWrap}>
                        <button
                          type="button"
                          style={{
                            ...styles.loadMoreBtn,
                            ...(visionLoadMorePending ? styles.loadMoreBtnDisabled : null),
                          }}
                          onClick={onLoadMoreVision}
                          disabled={visionLoadMorePending}
                          aria-label="Load more Vision Assist sessions"
                        >
                          {visionLoadMorePending ? "Loading…" : "Load more"}
                        </button>
                      </div>
                    )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* 🔻 Bottom Section */}
        <div
          style={{
            flex: "0 0 20%",
            minHeight: "70px",
            borderTop: "1px solid #ccc",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            padding: "10px 0",
          }}
        >
          {currentUser ? (
            <SidebarButton icon={<FiLogOut />} label={translate("logout").toUpperCase()} isCollapsed={isCollapsed} onClick={handleLogout} />
          ) : (
            <SidebarButton
              icon={<FiUser />}
              label={translate("login").toUpperCase()}
              isCollapsed={isCollapsed}
              onClick={() => {
                setIsSidebarOpen(false);
                navigate("/login");
              }}
            />
          )}

          {!isCollapsed && (
            <div style={{ marginTop: "15px", padding: "0 20px", paddingBottom: "env(safe-area-inset-bottom, 20px)" }}>
              <LanguageSelector />
            </div>
          )}
        </div>
      </div>

      {showSearchModalLocal && (
        <SearchChatModal
          chatList={chatList}
          onSelectChat={(chat) => {
            // ✅ immediate highlight
            const sid = chat?.session_id || "";
            if (sid) setPendingActive({ type: "chat", sid });

            setActiveVision?.(null);
            setActiveChat?.({ ...chat });
            navigate("/dashboard");
            closeSearchModal();
          }}
          onClose={closeSearchModal}
        />
      )}
    </>
  );
};

const SidebarLink = ({ to, icon, label, isCollapsed, onClick }) => (
  <Link to={to} style={styles.link} onClick={onClick}>
    <span style={styles.iconWrapper}>{icon}</span>
    <span className={`sidebar-label ${isCollapsed ? "collapsed" : ""}`}>{label}</span>
  </Link>
);

const SidebarButton = ({ icon, label, isCollapsed, onClick, isActive, trailingIcon = null }) => (
  <button
    type="button"
    style={{
      ...styles.link,
      ...(isActive ? styles.sectionActive : null),
      boxShadow: "none",
      outline: "none",
      border: "none",
      background: "none",
      width: "100%",
      textAlign: "left",
    }}
    onClick={onClick}
  >
    <span style={styles.iconWrapper}>{icon}</span>
    <span className={`sidebar-label ${isCollapsed ? "collapsed" : ""}`} style={styles.sidebarLabelText}>{label}</span>
    {trailingIcon ? <span style={styles.trailingIcon}>{trailingIcon}</span> : null}
  </button>
);

const SidebarSubLink = ({ to, label, isActive, onClick }) => (
  <Link
    to={to}
    style={{
      ...styles.subLink,
      ...(isActive ? styles.subLinkActive : null),
    }}
    onClick={onClick}
  >
    <span style={styles.subBullet} />
    <span style={styles.subLabel}>{label}</span>
  </Link>
);

const styles = {
  sidebar: {
    position: "fixed",
    top: 0,
    left: 0,
    bottom: 0,
    height: "100vh",
    background: "linear-gradient(180deg, #17245F 0%, #1E2A78 52%, #111A44 100%)",
    color: "#fff",
    overflowY: "auto",
    zIndex: 1001,
    transition: "all 0.3s ease",
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.3)",
    zIndex: 1000,
  },
  collapseBtn: {
    background: "none",
    border: "none",
    color: "#fff",
    cursor: "pointer",
    fontSize: "18px",
  },
  nav: {
    padding: "15px 0",
  },
  link: {
    display: "flex",
    alignItems: "center",
    padding: "10px 20px",
    color: "#fff",
    textDecoration: "none",
    cursor: "pointer",
  },
  sidebarLabelText: {
    flex: 1,
    minWidth: 0,
  },
  trailingIcon: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    opacity: 0.9,
  },
  subNavGroup: {
    margin: "2px 0 8px",
    paddingLeft: "30px",
    paddingRight: "12px",
  },
  subLink: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    color: "rgba(255,255,255,0.86)",
    textDecoration: "none",
    padding: "8px 10px 8px 16px",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
  },
  subLinkActive: {
    color: "#FFFFFF",
    background: "rgba(11, 95, 255, 0.28)",
    boxShadow: "inset 3px 0 0 rgba(255,255,255,0.85)",
  },
  subBullet: {
    width: "7px",
    height: "7px",
    borderRadius: "999px",
    background: "currentColor",
    opacity: 0.85,
    flex: "0 0 auto",
  },
  subLabel: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  iconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "24px",
    marginRight: "10px",
    fontSize: "18px",
  },

  // ✅ Chats header highlight style
  sectionActive: {
    background: "rgba(11, 95, 255, 0.22)",
    borderLeft: "3px solid rgba(255,255,255,0.85)",
  },

  historyContainer: {
    paddingTop: "10px",
  },

  loadMoreWrap: {
    display: "flex",
    justifyContent: "center",
    padding: "6px 20px 10px 54px",
  },
  loadMoreBtn: {
    width: "100%",
    minHeight: "34px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.24)",
    background: "rgba(255,255,255,0.10)",
    color: "#FFFFFF",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
    transition: "background 0.18s ease, border-color 0.18s ease, opacity 0.18s ease",
  },
  loadMoreBtnDisabled: {
    cursor: "default",
    opacity: 0.62,
  },

  threadIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "24px",
    marginLeft: "20px",
    marginRight: "10px",
    fontSize: "18px",
    opacity: 0.95,
  },

  chatItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    color: "#fff",
    cursor: "pointer",
    padding: "8px 20px",
    paddingLeft: 0,
  },

  // ✅ Stronger active highlight (works on the blue sidebar)
  activeItem: {
    background: "rgba(11, 95, 255, 0.22)",
    borderLeft: "3px solid rgba(255,255,255,0.85)",
  },

  chatTitle: {
    flexGrow: 1,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },

  menuWrapper: {
    marginLeft: "10px",
    position: "relative",
  },
  moreIcon: {
    cursor: "pointer",
  },

  dropdown: {
    position: "absolute",
    bottom: "26px",
    right: "0px",
    top: "auto",
    background: "#fff",
    color: "#000",
    boxShadow: "0 0 5px rgba(0,0,0,0.3)",
    zIndex: 9999,
    borderRadius: "8px",
    overflow: "hidden",
    minWidth: "140px",
    maxHeight: "260px",
    overflowY: "auto",
  },
  dropdownItem: {
    padding: "8px 12px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    whiteSpace: "nowrap",
  },

  visionItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    color: "#fff",
    cursor: "pointer",
    padding: "8px 20px",
    paddingLeft: 0,
  },
  visionTitleWrap: {
    flexGrow: 1,
    overflow: "hidden",
  },
  visionTitleLine: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  visionTitleText: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  visionPreview: {
    marginTop: "4px",
    opacity: 0.85,
    fontSize: "12px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    maxWidth: "210px",
  },
};

export default Sidebar;
